import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useRemoteConfig } from '_/context/RemoteConfigContext';
import {
  resumeQuote,
  ENDORSEMENTS_BLOCK,
  JUDICIAIS,
  TRABALHISTA_RECURSAL,
  DOCUMENT_TYPE
} from '_/utils';
import DocumentDownload from '_/modules/ProcessDetail/components/DocumentDownload';
import DocumentDetailWrapper from '_/modules/ProcessDetail/components/DocumentDetailWrapper';
import { isSuperUserLogged, getUserProfile } from '@services/auth';
import {
  hasModalityPermission,
  USER_TYPE,
  forceDownload
} from '@plataforma/utils';
import { ProcessApi } from '@services/api';
import { Snackbar } from '@plataforma/components';
import { useDocumentLink } from '../hooks/useDocumentLink';

function DocumentDetail({ onToastHandler, policy }) {
  const [remoteState] = useRemoteConfig();
  const [isDisabled, setDisabled] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    message: ''
  });
  const { handleCopy, handleDownload, getLink } = useDocumentLink();
  const [
    hasPermissionToReopenProcess,
    setHasPermissionToReopenProcess
  ] = useState(true);
  const {
    user: { userTypeDescription = '' }
  } = JSON.parse(window.localStorage.getItem('broker'));

  const {
    modalityId,
    subModalityId,
    process = {},
    quoteStatus = {},
    documentNumber,
    hasRestrictedAccess
  } = policy;

  const showDocumentStatus = ENDORSEMENTS_BLOCK.includes(quoteStatus.groupName);
  const labelProcess = Object.values(JUDICIAIS).includes(modalityId)
    ? 'judicial'
    : 'edital';
  const labelInsured = Object.values(TRABALHISTA_RECURSAL).includes(modalityId)
    ? 'judicial'
    : 'others';

  const verifyPermissions = useCallback(async () => {
    const policyGroupId = policy.quoteStatus.groupId;
    // Status.js - descrição dos grupos de status

    let hasPermission =
      !!(getUserProfile() === USER_TYPE.broker || USER_TYPE.commercial) ||
      (await hasModalityPermission(modalityId, subModalityId));
    hasPermission = hasPermission && policyGroupId === 1;
    hasPermission =
      hasPermission || (policyGroupId === 6 && isSuperUserLogged());
    hasPermission =
      hasPermission &&
      !(!remoteState.isSusepValidated && remoteState.config.susepCheck);
    hasPermission = hasPermission && process.type !== DOCUMENT_TYPE.policy;
    hasPermission = hasPermission && policy.modalityId !== 123;

    setHasPermissionToReopenProcess(hasPermission);
  }, [
    remoteState.config.susepCheck,
    remoteState.isSusepValidated,
    modalityId,
    subModalityId,
    process.type,
    policy.quoteStatus.groupId,
    policy.modalityId
  ]);

  const getDocumentLink = useCallback(
    async currDocumentNumber => {
      await getLink(currDocumentNumber);
    },
    [getLink]
  );

  async function handleCopyLink() {
    const url = await handleCopy();
    onToastHandler({ isOpen: !!url });
  }

  function handleActionButtonDetails() {
    resumeQuote(policy, remoteState.config.v3Settings);
  }

  async function handlePdfButtonDetails() {
    setDisabled(true);
    try {
      const response = await ProcessApi.getPdfInfo(policy);

      const file = new Blob([response.data], {
        type: 'application/pdf'
      });
      const fileURL = URL.createObjectURL(file);

      forceDownload(fileURL, `process ${policy.process.processId}.pdf`);
      return;
    } catch (e) {
      const utf8decoder = !('TextEncoder' in window) ? null : new TextDecoder();
      const arrayBufferDecoded =
        (utf8decoder && utf8decoder.decode(e.data)) || null;
      const errorArray = JSON.parse(arrayBufferDecoded);

      if (errorArray) {
        errorArray.errors.forEach(item => {
          setToast({
            open: true,
            variant: 'error',
            message: item.message || 'Erro ao baixar o PDF'
          });
        });
      } else {
        setToast({ open: true, variant: 'error', message: '' });
      }
    } finally {
      setDisabled(false);
    }
  }

  useEffect(() => {
    if (documentNumber) {
      getDocumentLink(documentNumber);
    }
  }, [getDocumentLink, documentNumber]);

  const checkDownload = () => {
    if (policy.modalityId === 123) {
      return false;
    }

    return process.type !== 'quote' && !hasRestrictedAccess;
  };

  function renderDocumentDownload() {
    return (
      <DocumentDownload
        type={process.type}
        onCopyDocumentLink={handleCopyLink}
        onDownloadDocument={handleDownload}
        quoteStatus={policy.quoteStatus}
      />
    );
  }

  useEffect(() => {
    verifyPermissions();
  }, [verifyPermissions]);

  return (
    <>
      <DocumentDetailWrapper
        process={process}
        onClickActionButton={handleActionButtonDetails}
        onClickPdfButton={handlePdfButtonDetails}
        showDocumentStatus={showDocumentStatus}
        labelProcess={labelProcess}
        labelInsured={labelInsured}
        userType={userTypeDescription}
        hasPermissionToReopenProcess={hasPermissionToReopenProcess}
        isDisabled={isDisabled}
        {...policy}
      >
        {checkDownload() && renderDocumentDownload()}
      </DocumentDetailWrapper>
      <Snackbar
        open={toast.open}
        message={toast.message}
        variant={toast.variant}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        data-testid="extract-snackbar"
      />
    </>
  );
}

DocumentDetail.propTypes = {
  policy: PropTypes.shape({
    documentNumber: PropTypes.string,
    modalityId: PropTypes.number,
    subModalityId: PropTypes.number,
    quoteStatus: PropTypes.shape({
      groupId: PropTypes.number,
      groupName: PropTypes.string
    }),
    process: PropTypes.shape({
      processId: PropTypes.string,
      type: PropTypes.string
    }),
    hasRestrictedAccess: PropTypes.bool
  }),
  onToastHandler: PropTypes.func
};

export default memo(DocumentDetail);
